import React from 'react'
import styled from '@emotion/styled'
import { NavLink } from 'react-router-dom'
import * as constants from 'view/styles/constants'
import { Tooltip } from 'view/components/tooltip'
import { iconSet } from 'view/components/icons'
import { css } from '@emotion/react'
import { removeUrlParamPlaceholders } from 'view/routes'

export interface LeftNavigationTileLinkProps {
  label: string
  numberOfItems: number | null
  showActivityHighlight: boolean | null
  showAlertHighlight: boolean | null
  showWarnHighlight: boolean | null
  url: string
  icon?: JSX.Element
  isLeftNavigationMinimised: boolean
  isSecondary: boolean
  isCollapsed?: boolean
  isActive?: () => boolean
  onClick?: () => void
  onToggleCollapsed?: () => void
  dataTestId?: string
}

const Content = styled.div<{ hasIcon: boolean; isSecondary: boolean }>`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 34px;
  border-top-left-radius: 17px;
  border-bottom-left-radius: 17px;
  background-color: transparent;
  border-right: none;
  padding-top: 3px;
  padding-right: 3px;
  padding-left: ${props => (props.hasIcon ? '3px' : '9px')};
  padding-bottom: 3px;
  color: ${props => (props.isSecondary ? constants.CORE_PRIMARY_5 : constants.CORE_PRIMARY_6)};

  a.active & {
    background-color: ${constants.ACCENT_PRIMARY};
    color: ${constants.CORE_PRIMARY};
  }

  a:not(.active):hover & {
    background-color: ${constants.CORE_PRIMARY_1};
  }
`

const iconCircle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  height: 100%;
  outline: none;
  margin-right: 9px;
  border-radius: 50%;
  background-color: white;
`

const Icon = styled.div<{}>`
  ${iconCircle}
  width: 26px;
  height: 26px;
  svg {
    width: 22px;
    height: 22px;
    color: ${constants.CORE_PRIMARY_5};
  }

  a.active & svg,
  a:hover & svg {
    color: ${constants.ACCENT_PRIMARY};
  }
`

const Chevron = styled.div<{ isCollapsed?: boolean }>`
  ${iconCircle}
  width: 17px;
  height: 17px;
  background-color: transparent;

  &:hover {
    background-color: white;
  }

  svg {
    width: 11px;
    height: 11px;
    transform: rotate(${props => (props.isCollapsed ? 0 : '180deg')});
    margin-top: ${props => props.isCollapsed && '3px'};
    visibility: hidden;
  }

  a:hover & svg {
    color: ${constants.ACCENT_PRIMARY};
    visibility: visible;
  }
  a.active:hover & svg {
    color: white;
    visibility: visible;
  }
  a.active &:hover {
    svg {
      color: ${constants.ACCENT_PRIMARY};
    }
  }
`

const Label = styled.div<{ isSecondary: boolean }>`
  overflow: hidden;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  outline: none;
  font-family: NEXWeb;
  font-size: ${props => (props.isSecondary ? '13px' : '14px')};
  font-weight: ${props => (props.isSecondary ? constants.FontWeight.Normal : constants.FontWeight.Medium)};
`

const Highlight = styled.div`
  align-self: flex-start;
  margin-right: 9px;
  margin-top: 3px;
  width: 10px;
  height: 10px;
  border-radius: 3px;
`

const ActivityHighlight = styled(Highlight)`
  background-color: ${constants.ACCENT_PRIMARY};
`

const AlertHighlight = styled(Highlight)`
  background-color: ${constants.ACCENT_BAD};
`

const WarnHighlight = styled(Highlight)`
  background-color: ${constants.CORE_PRIMARY_5};
`

export const LeftNavigationTileLink: React.FC<LeftNavigationTileLinkProps> = props => {
  const {
    label,
    numberOfItems,
    showActivityHighlight,
    showAlertHighlight,
    showWarnHighlight,
    url,
    icon,
    isLeftNavigationMinimised,
    isSecondary,
    isCollapsed,
    onToggleCollapsed,
    isActive,
    onClick,
    dataTestId
  } = props

  const labelForDisplay = numberOfItems ? `${label} (${numberOfItems})` : label

  const onToggleButtonClick = (ev: React.MouseEvent) => {
    if (onToggleCollapsed) {
      ev.preventDefault()
      ev.stopPropagation()
      onToggleCollapsed()
    }
  }

  return (
    <NavLink
      data-test-id={dataTestId}
      id={url}
      to={removeUrlParamPlaceholders(url)}
      className={props => {
        if (isActive) {
          return isActive() ? 'active' : ''
        } else {
          return props.isActive ? 'active' : ''
        }
      }}
      end={!isLeftNavigationMinimised}
      onClick={onClick}>
      <Content hasIcon={!!icon} isSecondary={isSecondary}>
        {isLeftNavigationMinimised ? (
          <Tooltip tooltip={label}>
            <Icon>{icon}</Icon>
          </Tooltip>
        ) : (
          <>
            {icon && <Icon>{icon}</Icon>}
            <Tooltip tooltip={label}>
              <Label isSecondary={isSecondary}>{labelForDisplay}</Label>
            </Tooltip>
            {showActivityHighlight && <ActivityHighlight />}
            {showAlertHighlight && <AlertHighlight />}
            {showWarnHighlight && <WarnHighlight />}
            {onToggleCollapsed && (
              <Chevron onClick={onToggleButtonClick} isCollapsed={isCollapsed}>
                {iconSet.chevronDown}
              </Chevron>
            )}
          </>
        )}
      </Content>
    </NavLink>
  )
}
